import { Injectable, OnDestroy } from '@angular/core';
import { Order, OrderHeader } from '@usf/ngrx-order';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Observable,
  Subscription,
} from 'rxjs';
import { OrderService } from '@order/services/order.service';
import { ProductReview } from '@shared/models/product-review.model';
import { ProductService } from '@shared/services/product/product.service';
import {
  RequestCredit,
  RequestCreditAttachment,
  IssueDetails,
  IssueOptions,
  PickupRequest,
  RecoveryRequest,
} from '@usf/list-types';
import {
  ServiceRequestFacadeService,
  SERVICE_REQUEST_ACTIONS,
  submitRequestCreditTransformer,
  submitPickupRequestTransformer,
} from '@usf/ngrx-list';
import { FileService } from '@shared/services/file/file.service';
import { PhotoService } from '@shared/services/photo/photo.service';
import {
  UserFile,
  FILE_ERRORS,
  FILE_ERROR_TOAST_MESSAGE,
} from '@shared/services/file/model/file.model';
import { UserPhoto } from '@shared/services/photo/model/photo.model';
import { ToastService } from '@shared/services/toast/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { debounceTime, filter, first, map, take, tap } from 'rxjs/operators';
import { RequestCreditData } from '@service-request/pages/request-credit/models/request-credit.model';
import { Product, ProductPropertiesEnum } from '@usf/product-types';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { FEATURES } from '@shared/constants/splitio-features';
import { Router } from '@angular/router';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { isDateGreater } from '@shared/helpers/calendar.helpers';
import {
  NeverPickUpOneClass,
  NeverPickUpOneGroup,
  NeverPickUpOneStorage,
  NeverPickUpStockYardBrand,
  NeverPickUpThreeClass,
  NeverPickUpThreeStorage,
  NeverPickUpTwoStorage,
  NeverpPckUpTwoClass,
  SevenDaysFrozenClass,
  SevenDaysFrozenStorage,
  SevenDaysNotFrozen,
} from '@shared/constants/pick-up-request-values';
import { User, UserKinds } from '@usf/user-types/user';
import { ListAnalyticsService } from '@usf/ngrx-list';
import { DatePipe } from '@angular/common';
import { UserService } from '@app/user/services';

@Injectable({
  providedIn: 'root',
})
export class ServiceRequestService implements OnDestroy {
  orders$: Observable<{ myOrders: Order[]; ordersLoaded: boolean }>;
  products$: Observable<ProductReview[]>;
  issues$: Observable<IssueOptions[]>;

  showRequiredProductReceivedInput: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  showRequiredCommentsInput: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  blockSubmission: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public showPickUpQuestionModal: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  public withIcon = true;
  public pickupQuestion1 = 'title';
  public pickUpQuestion2 = 'question';
  public pickUpLeftButton = 'left';
  public pickUpRightButton = 'right';
  public leftIsTrue = true;
  public isFrozenModal = true;

  public modalRelatedParams: RequestCreditData = {
    quantityUnits: 'CS',
  } as RequestCreditData;
  public selectedParams: RequestCreditData = {
    quantityUnits: 'CS',
  } as RequestCreditData;

  isCESProduct = false;
  isDirectProduct = false;
  missingAttachment = false;
  attachmentRequired = true;
  commentsRequired = false;
  isPickUp = false;
  userEmail: string;
  issueDetails: IssueDetails[];
  issueDetailsForReset: IssueDetails[];
  debounceTime = 500;
  searchKey = '';
  maxPhotos = 3;

  productNumbersSub$: Subscription = new Subscription();
  productsSub$: Subscription = new Subscription();
  selectedProductSub$: Subscription = new Subscription();
  searchCheckSub$: Subscription = new Subscription();
  tempProductSubForPickupCheck$ = new Subscription();

  trackingData$: BehaviorSubject<{
    orderId: string;
    hasPhoto: boolean;
    productData: any[];
    hasDocument: boolean;
  }> = new BehaviorSubject(null);

  constructor(
    private router: Router,
    public translateService: TranslateService,
    public orderService: OrderService,
    public productService: ProductService,
    public serviceRequestService: ServiceRequestFacadeService,
    private loadingSpinnerService: LoadingSpinnerService,
    public toastService: ToastService,
    public fileService: FileService,
    public photoService: PhotoService,
    private store: Store<State>,
    private platformService: PlatformService,
    public panAppState: PanAppState,
    private listAnalyticsService: ListAnalyticsService,
    private datePipe: DatePipe,
  ) {
    this.updateSelectedParams({ quantityUnits: 'CS' } as RequestCreditData);
    this.photoService.loadPhotos();
    this.fileService.loadFiles();
  }

  // Order and Product Functions
  getRequestCreditOrders() {
    this.orders$ = this.orderService.loadRequestCreditOrders$(null);
  }

  getProducts(selectedOrder: Order) {
    if (this.productNumbersSub$) {
      this.productNumbersSub$.unsubscribe();
    }

    this.productNumbersSub$ = this.productService
      .getProductNumbersById$(this.getOrderId(selectedOrder))
      .subscribe(numbers => {
        this.productService.loadProducts(numbers);
        this.products$ = this.productService.getProductReviewByNumbers$(
          numbers,
          this.searchKey === '' ? undefined : this.searchKey,
        );
        this.getProductsFromSearchKey();
      });
  }

  getProductsFromSearchKey() {
    if (this.searchCheckSub$) {
      this.searchCheckSub$.unsubscribe();
    }

    this.searchCheckSub$ = this.products$.subscribe(values => {
      if (
        values.find(
          value =>
            Number(this.modalRelatedParams.productNumber) ===
            Number(value.productNumber),
        ) === undefined
      ) {
        this.modalRelatedParams = {
          ...this.modalRelatedParams,
          productNumber: undefined,
          product: undefined,
        };
      }
    });
  }

  getOrderTypeLabel(order: Order): string {
    return order
      ? 'i18n.requestCredit.orderType' + order?.orderHeader?.orderType
      : '-';
  }

  onOrderSearch(event: any) {
    this.searchKey = event?.detail?.value?.toString();
    if (this.searchKey) {
      this.orders$ = this.orderService.loadRequestCreditOrders$(this.searchKey);
    } else {
      this.orders$ = this.orderService.loadRequestCreditOrders$(null);
    }
  }

  onProductSearch(selectedOrder: Order) {
    this.getProducts(selectedOrder);
  }

  getOrderId(selectedOrder: Order) {
    return (
      selectedOrder?.orderHeader?.customerNumber?.toString() +
      ':' +
      selectedOrder?.orderHeader?.departmentNumber?.toString() +
      ':' +
      selectedOrder?.orderHeader?.orderId
    );
  }

  setModalOrderAndProduct(
    order: Order | undefined,
    productReview: ProductReview | undefined,
  ) {
    let toSet: RequestCreditData = {
      order: order,
      product: productReview,
      productNumber: productReview ? productReview.productNumber : undefined,
      photos: [],
      issue: undefined,
      productInOriginalPackage: undefined,
      quantityUnits: 'CS',
    } as RequestCreditData;

    if (productReview) {
      this.selectedProductSub$ = this.productService
        .getProduct(productReview.productNumber)
        .subscribe(prod => {
          if (prod?.summary?.properties.size) {
            this.isCESProduct = prod?.summary?.properties?.has(
              ProductPropertiesEnum.ces,
            )
              ? true
              : false;
            this.isDirectProduct = prod?.summary?.properties?.has(
              ProductPropertiesEnum.direct,
            )
              ? true
              : false;
          }
        });
    }

    this.updateModalParams(toSet);
  }

  checkProduct = (productReview: ProductReview, order: Order) => {
    this.tempProductSubForPickupCheck$ = combineLatest([
      this.panAppState?.feature$([FEATURES.split_global_pickup_request.name]),
      this.productService.getProduct(productReview.productNumber),
    ])
      .pipe(take(1))
      .subscribe(([flag, product]) => {
        this.productCanBePickedUp(product, flag, order);
      });
  };

  productCanBePickedUp = (product: Product, flag: boolean, order: Order) => {
    if (!flag || order.orderHeader.orderType === 'WC') {
      this.isPickUp = false;
      return;
    }

    if (
      this.cannotPickupOne(product) ||
      this.cannotPickupTwo(product) ||
      this.cannotPickupThree(product) ||
      this.cannotPickupBrand(product) ||
      this.cannotPickupType(product)
    ) {
      this.isPickUp = false;
    } else if (this.canPickupNotFrozenBut7Days(product)) {
      if (
        this.checkDateValidityForPickup(order.orderHeader.confirmedDeliveryDate)
      ) {
        this.setPickUpValues();
      } else {
        this.isPickUp = false;
      }
    } else if (this.canPickupFrozenBut7Days(product)) {
      if (
        this.checkDateValidityForPickup(order.orderHeader.confirmedDeliveryDate)
      ) {
        this.setPickUpValuesForFrozen();
      } else {
        this.isPickUp = false;
      }
    } else {
      this.setPickUpValues();
    }
  };

  checkDateValidityForPickup = (deliveryDate: Date | string) => {
    const delDate = new Date(deliveryDate);
    delDate.setHours(delDate.getHours() + delDate.getTimezoneOffset() / 60);
    delDate.setDate(delDate.getDate() + 7);
    const deliveryCheck = new Date(delDate);
    const today = new Date();

    return isDateGreater(deliveryCheck, today);
  };

  canPickupNotFrozenBut7Days = (product: Product): boolean => {
    return SevenDaysNotFrozen.includes(product.summary.classCode);
  };

  canPickupFrozenBut7Days = (product: Product): boolean => {
    return (
      SevenDaysFrozenClass.includes(product.summary.classCode) &&
      SevenDaysFrozenStorage === product.summary.groupStorageCode
    );
  };

  cannotPickupOne = (product: Product): boolean => {
    const classCheck = NeverPickUpOneClass.includes(product.summary.classCode);
    const groupCheck = NeverPickUpOneGroup.includes(product.summary.groupCode);
    const storageCheck = NeverPickUpOneStorage.includes(
      product.summary.groupStorageCode,
    );
    return classCheck && groupCheck && storageCheck;
  };

  cannotPickupTwo = (product: Product): boolean => {
    const classCheck = NeverpPckUpTwoClass.includes(product.summary.classCode);
    const storageCheck = NeverPickUpTwoStorage.includes(
      product.summary.groupStorageCode,
    );
    return classCheck && storageCheck;
  };

  cannotPickupThree = (product: Product): boolean => {
    const classCheck = NeverPickUpThreeClass.includes(
      product.summary.classCode,
    );
    const storageCheck = NeverPickUpThreeStorage.includes(
      product.summary.groupStorageCode,
    );
    return classCheck && storageCheck;
  };

  cannotPickupType = (product: Product): boolean => {
    return (
      product.summary.properties.has(ProductPropertiesEnum.ces) ||
      product.summary.properties.has(ProductPropertiesEnum.jit) ||
      product.summary.properties.has(ProductPropertiesEnum.direct)
    );
  };

  cannotPickupBrand = (product: Product): boolean => {
    return product.summary?.brand
      .toLowerCase()
      .includes(NeverPickUpStockYardBrand);
  };

  setPickUpValues = () => {
    this.withIcon = false;
    this.leftIsTrue = true;
    this.pickupQuestion1 = 'i18n.requestCredit.pickUp.regTitle';
    this.pickUpQuestion2 = '';
    this.pickUpLeftButton = 'i18n.requestCredit.pickUp.regLeft';
    this.pickUpRightButton = 'i18n.requestCredit.pickUp.regRight';
    this.isFrozenModal = false;
    this.showPickUpQuestionModal.next(true);
  };

  setPickUpValuesForFrozen = () => {
    this.withIcon = true;
    this.leftIsTrue = false;
    this.pickupQuestion1 = 'i18n.requestCredit.pickUp.frozenTitle';
    this.pickUpQuestion2 = 'i18n.requestCredit.pickUp.frozenSubTitle';
    this.pickUpLeftButton = 'i18n.requestCredit.pickUp.frozenLeft';
    this.pickUpRightButton = 'i18n.requestCredit.pickUp.frozenRight';
    this.isFrozenModal = true;
    this.showPickUpQuestionModal.next(true);
  };

  handlePickupQuestionDismiss = (value: boolean) => {
    this.isPickUp = value;
    this.closePickUpQuestion();
  };

  closePickUpQuestion = () => {
    this.showPickUpQuestionModal.next(false);
  };

  getIssues() {
    if (this.isPickUp) {
      this.issues$ = combineLatest([
        this.serviceRequestService.pickupRequestIssueOptions$(),
        this.panAppState.user$
          .pipe(debounceTime(600), take(1))
          .pipe(map(user => user?.userKind)),
      ]).pipe(
        map(([issues, userKind]) => {
          let issueToReturn = issues.filter(issue => {
            return (
              !(userKind !== UserKinds.Internal && issue.internalUserOnly) ||
              userKind === UserKinds.Internal
            );
          });

          const filteredIssue = issueToReturn.map(issue => {
            const temp = issue.issueDetails.filter(detail => {
              return (
                !(userKind !== UserKinds.Internal && detail.internalUserOnly) ||
                userKind === UserKinds.Internal
              );
            });
            return { ...issue, issueDetails: temp };
          });
          return filteredIssue;
        }),
      );
    } else {
      this.issues$ = this.serviceRequestService.requestCreditIssueOptions$();
    }
  }

  getIssueDetails(issue: IssueOptions) {
    this.issueDetails = issue.issueDetails;
  }

  setModalIssueAndDetails(
    issue: IssueOptions | undefined,
    detail: IssueDetails | undefined,
  ) {
    if (this.selectedParams?.issue !== issue) {
      this.showRequiredProductReceivedInput.next(false);
      this.showRequiredCommentsInput.next(false);
    }

    let toSet: RequestCreditData = {
      ...this.selectedParams,
      issue: issue,
      issueDetails: detail,
      issueDetailsText: detail ? detail.displayText : undefined,
      productReceived:
        this.selectedParams?.issue !== issue
          ? undefined
          : this.selectedParams.productReceived,
    };
    this.updateModalParams(toSet);
  }

  //Email Address Functions
  async populateUserEmail() {
    console.log('requesting user');
    let user = await firstValueFrom(
      this.panAppState.user$.pipe(
        debounceTime(600),
        filter(user => {
          return !!user;
        }),
        take(1),
      ),
    );
    console.log('user found ', user);
    this.userEmail = user?.email || '';
    console.log('this is the email: ', this.userEmail);
    this.setEmail(this.userEmail);
  }

  setEmail(email: string) {
    let toSet = {
      ...this.selectedParams,
      email,
    };
    this.updateSelectedParams(toSet);
  }

  // Quantity and Unit Functions
  setQuantity(quantity: number) {
    let toSet = {
      ...this.selectedParams,
      quantity,
    };
    this.updateSelectedParams(toSet);
  }

  setQuantityUnits(quantityUnits: string) {
    let toSet = {
      ...this.selectedParams,
      quantityUnits,
    };
    this.updateSelectedParams(toSet);
  }

  setReSeal(productInOriginalPackage: boolean) {
    let toSet = {
      ...this.selectedParams,
      productInOriginalPackage,
    };
    this.updateSelectedParams(toSet);
  }

  // Photo and File Functions
  setPhotos(photos: UserPhoto[]) {
    if (photos.length > 0) {
      this.missingAttachment = false;
    }
    let toSet = {
      ...this.selectedParams,
      photos,
    };
    this.updateSelectedParams(toSet);
  }

  setFile(file: UserFile) {
    if (file !== undefined) {
      this.missingAttachment = false;
    }
    let toSet = {
      ...this.selectedParams,
      file,
    };
    this.updateSelectedParams(toSet);
  }

  async onAddPhoto() {
    try {
      let photo = await this.photoService.addPhoto();
      let photos = [];
      if (
        this.selectedParams.photos &&
        this.selectedParams.photos.length >= this.maxPhotos
      ) {
        photos = this.selectedParams.photos.slice(1);
      } else if (this.selectedParams.photos) {
        photos = Array.from(this.selectedParams.photos);
      }

      photos.push(photo);
      this.setPhotos(photos);
    } catch (error) {
      this.handleFileErrors(error.message);
    }
  }

  async onPhotoChange(newPhotos: UserPhoto[]) {
    let photosToDelete = this.selectedParams.photos.filter(photo => {
      return !newPhotos.includes(photo);
    });
    for (let j = 0; j < photosToDelete.length; j++) {
      try {
        await this.photoService.deletePhoto(photosToDelete[j]);
      } catch (ignore) {
        // Photo may already be deleted
      }
    }
  }

  async onDeletePhoto(photo: UserPhoto) {
    this.setPhotos(this.selectedParams.photos.filter(value => value !== photo));
  }

  async onAddFile() {
    try {
      let file = await this.fileService.addFile();
      this.setFile(file);
    } catch (errorToToast) {
      this.handleFileErrors(errorToToast.message);
    }
  }

  async onDropFile(event: any) {
    try {
      let file = await this.fileService.dropFile(event);
      this.setFile(file);
    } catch (errorToToast) {
      this.handleFileErrors(errorToToast.message);
    }
  }

  async onDeleteFile() {
    this.setFile(undefined);
  }

  // Confirmation Functions
  handleFileErrors(message: any) {
    this.fileService.isAfileOperationInProcess = false;
    switch (message) {
      case FILE_ERRORS.canceled.valueOf():
        break;
      case FILE_ERRORS.delete.valueOf():
        break;
      case FILE_ERRORS.wrongSize.valueOf():
        this.presentToast(FILE_ERROR_TOAST_MESSAGE.wrongSize.valueOf());
        break;
      case FILE_ERRORS.wrongMime.valueOf():
        this.presentToast(FILE_ERROR_TOAST_MESSAGE.mimeMismatch.valueOf());
        break;
      case FILE_ERRORS.missingAttachment.valueOf():
        this.presentToast(FILE_ERROR_TOAST_MESSAGE.missingAttachment.valueOf());
        break;
      default:
        this.presentToast(FILE_ERROR_TOAST_MESSAGE.general.valueOf());
    }
  }

  presentToast(messageToTranslate: any) {
    this.toastService.presentToastMsg(
      this.translateService.instant(messageToTranslate),
      'red-toast',
      MessageTypeEnum.error,
      [],
    );
  }

  verifyRequest(): boolean {
    let requestISValid = true;
    if (this.attachmentRequired) {
      if (
        (this.selectedParams.photos === undefined ||
          this.selectedParams.photos.length === 0) &&
        this.selectedParams.file === undefined
      ) {
        this.missingAttachment = true;
        this.handleFileErrors(FILE_ERRORS.missingAttachment.valueOf());
        requestISValid = false;
      }
    }

    if (
      this.commentsRequired &&
      (this.selectedParams.comments === undefined ||
        this.selectedParams.comments === '')
    ) {
      requestISValid = false;
    }
    return requestISValid;
  }

  async handleRequestConfirmation(trackingData: any) {
    if (this.isPickUp) {
      this.listAnalyticsService.trackSubmitPickupRequest();
      const rcObject = await this.transformPickupRequest();
      this.loadingSpinnerService.createSpinnerModal().then(() => {
        this.store.dispatch(
          SERVICE_REQUEST_ACTIONS.submitPickupRequest({
            pickupRequest: rcObject,
            trackingData,
            tracking: {
              tracing: {
                data: {
                  traceContext: 'pickupRequest',
                  isEndOfTrace: false,
                  isStartOfTrace: true,
                  attributes: {
                    productNumber: Number(rcObject.apn),
                    orderId: rcObject.orderNbr,
                    event: 'submit pickup request',
                    spanName: 'Submit Pickup Request',
                  },
                },
                transformFunc: submitPickupRequestTransformer,
              },
            },
          }),
        );
      });
    } else {
      if (
        this.selectedParams.issueDetails &&
        isNaN(Number(this.selectedParams.issueDetails.code))
      ) {
        this.presentToast('i18n.pickUp.failureMessage');
        return;
      }
      const rcObject = await this.transformRequestCredit();
      this.loadingSpinnerService.createSpinnerModal().then(() => {
        this.store.dispatch(
          SERVICE_REQUEST_ACTIONS.submitRequestCredit({
            requestCredit: rcObject,
            trackingData,
            tracking: {
              tracing: {
                data: {
                  traceContext: 'requestCredit',
                  isEndOfTrace: false,
                  isStartOfTrace: true,
                  attributes: {
                    productNumber: Number(rcObject.apn),
                    orderId: rcObject.orderNbr,
                    event: 'submit request credit',
                    spanName: 'Submit Request Credit',
                  },
                },
                transformFunc: submitRequestCreditTransformer,
              },
            },
          }),
        );
      });
    }
  }

  getResealable = () => {
    return this.selectedParams?.issueDetails?.productResaleable
      ? this.selectedParams?.productInOriginalPackage
      : false;
  };

  async transformPickupRequest() {
    let attachments = await this.convertFilesToAttachments();

    const [customerNumber, divisionNumber, webName, userEmail, recID] =
      await firstValueFrom(
        combineLatest([
          this.panAppState.customer$,
          this.serviceRequestService.serviceRequest$(),
        ]).pipe(
          take(1),
          map(([customer, selectServiceRequestState]) => {
            return [
              customer.customerNumber,
              customer.divisionNumber,
              customer.customerName,
              this.userEmail,
              selectServiceRequestState.serviceRequest.RoutedOrder.recordTypeId,
              selectServiceRequestState.serviceRequest.RoutedOrder
                .serviceRequestReason,
              selectServiceRequestState.serviceRequest.RoutedOrder.type,
            ];
          }),
        ),
      );

    let pickupRequest: PickupRequest = {
      apn: this.selectedParams.productNumber.toString(),
      orderNbr:
        this.selectedParams.order.orderHeader.tandemOrderNumber.toString(),
      files: attachments.file_array,
      images: attachments.photo_array,
      servReqRsn: 'Pickup (Return)',
      pickupRsnCd: this.selectedParams.issueDetails.reasonCode,
      isProdReslbleCondn: this.getResealable() ? 'YES' : 'NO',
      subject: 'Initiated by Ecom',
      desc: this.selectedParams.comments,
      source:
        this.platformService.platformType ===
        this.platformService.platformEnum.desktop
          ? 'Ecom Desktop'
          : 'Ecom Mobile',
      priority: 'Medium',
      dueDate: new Date().toISOString(),
      tandemAcntNbr: customerNumber,
      divisionNumber: divisionNumber,
      origin: 'Web',
      webName: webName,
      webEmail: userEmail,
      caseStatus: 'NEW',
      caseType: 'Customer Maintenance',
      sendEmailNot: 'FALSE',
      recTypeId: recID,
      invNbr:
        this.selectedParams?.order?.orderHeader?.invoiceNumber?.toString(),
      quantityAmt: Number(this.selectedParams?.quantity),
      quantityType:
        this.selectedParams?.quantityUnits === 'CS' ? 'Cases' : 'Each',
      invDate: this.selectedParams?.order?.orderHeader?.invoiceDate?.toString(),
    };
    return pickupRequest;
  }

  async submitRecoveryRequest(currentOrder: Order, request: RecoveryRequest) {
    const rcObject = await this.transformRecoveryRequest(currentOrder, request);
    this.loadingSpinnerService.createSpinnerModal().then(() => {
      this.store.dispatch(
        SERVICE_REQUEST_ACTIONS.submitRecoveryOrder({
          recoveryRequest: rcObject,
        }),
      );
    });
  }

  async transformRecoveryRequest(
    currentOrder: Order,
    request: RecoveryRequest,
  ) {
    const [
      customerNumber,
      divisionNumber,
      firstName,
      lastName,
      userEmail,
      recordTypeId,
    ] = await firstValueFrom(
      combineLatest([
        this.panAppState.customer$,
        this.panAppState.user$,
        this.serviceRequestService.serviceRequest$(),
      ]).pipe(
        take(1),
        map(([customer, user, selectServiceRequestState]) => {
          return [
            customer.customerNumber,
            customer.divisionNumber,
            user.firstName,
            user.lastName,
            request.webEmail ? request.webEmail : user.email,
            selectServiceRequestState.serviceRequest.recoveryRequest
              ?.recordTypeId,
          ];
        }),
      ),
    );

    let recoveryRequest: RecoveryRequest = {
      subject: 'Initiated by Ecom',
      desc: request.desc,
      source:
        this.platformService.platformType ===
        this.platformService.platformEnum.desktop
          ? 'Ecom Desktop'
          : 'Ecom Mobile',
      priority: 'High',
      servReqRsn: 'Recovery Express',
      recvryRsn: request.recvryRsn,
      recvryDelMethd: 'Vehicle (Courier/Van)',
      reqDelvryDt: this.convertDateStringToYYYYMMDD(request.reqDelvryDt),
      amPmRtePref: this.getRecoveryDeliveryText(request.reqDelvryDt),
      orderNbr: currentOrder?.orderHeader?.tandemOrderNumber
        ? currentOrder?.orderHeader?.tandemOrderNumber.toString()
        : '',
      caseQuantity: this.findTotalUnitsOrdered(currentOrder),
      tandemAcntNbr: customerNumber,
      divisionNumber: divisionNumber,
      origin: 'Web',
      webName: (firstName ? firstName : '') + ' ' + (lastName ? lastName : ''),
      webEmail: userEmail,
      recvryStatus: 'New',
      recvryType: 'Modify Order',
      sendEmailNot: 'FALSE',
      recTypeId: recordTypeId,
    };
    return recoveryRequest;
  }

  findTotalUnitsOrdered(order: Order): number {
    let totalUnits = 0;
    if (order.orderItems) {
      order.orderItems.forEach(item => {
        totalUnits = totalUnits + Number(item.unitsOrdered.currentValue || 0);
      });
    }
    return totalUnits;
  }

  getRecoveryDeliveryText(dateString: string) {
    if (dateString.includes('Today')) {
      return 'Recovery Delivery Same Day';
    } else if (dateString.includes('Tomorrow')) {
      return 'Recovery Delivery Next Day';
    } else return '';
  }

  convertDateStringToYYYYMMDD(dateString: string): string {
    // Extract the date value Example Input "Today (10/22/2024)"
    const datePart = dateString.match(/\((\d{1,2}\/\d{1,2}\/\d{4})\)/)?.[1];
    //Output "10/22/2024"
    if (datePart) {
      //using DatePipe to convert the date to YYYY-MM-DD format
      const formattedDate = this.datePipe.transform(datePart, 'yyyy-MM-dd');

      //Output "2024-10-22"
      return formattedDate;
    }
    return '';
  }

  // make a transform for pickup request
  async transformRequestCredit() {
    let attachments = await this.convertFilesToAttachments();

    const [
      customerNumber,
      divisionNumber,
      webName,
      userEmail,
      recID,
      serviceReason,
      reqType,
    ] = await firstValueFrom(
      combineLatest([
        this.panAppState.customer$,
        this.serviceRequestService.serviceRequest$(),
      ]).pipe(
        take(1),
        map(([customer, selectServiceRequestState]) => {
          return [
            customer.customerNumber,
            customer.divisionNumber,
            customer.customerName,
            this.userEmail,
            selectServiceRequestState.serviceRequest.RoutedOrder.recordTypeId,
            selectServiceRequestState.serviceRequest.RoutedOrder
              .serviceRequestReason,
            selectServiceRequestState.serviceRequest.RoutedOrder.type,
          ];
        }),
      ),
    );

    let requestCredit: RequestCredit = {
      subject: 'Initiated by Ecom',
      desc: this.selectedParams.comments,
      source:
        this.platformService.platformType ===
        this.platformService.platformEnum.desktop
          ? 'Ecom Desktop'
          : 'Ecom Mobile',
      priority: 'Medium',
      servReqRsn: serviceReason,
      cdmrRsnCode: this.selectedParams.issue
        ? this.selectedParams.issueDetails.code +
          '-' +
          this.selectedParams.issueDetails.reasonCode
        : '',
      scnRC: this.selectedParams.issue
        ? this.selectedParams.issueDetails.name
        : '',
      apn: this.selectedParams.productNumber.toString(),
      orderNbr:
        this.selectedParams.order.orderHeader.tandemOrderNumber.toString(),
      files: attachments.file_array,
      images: attachments.photo_array,
      exptProdCt: 1,
      tandemAcntNbr: customerNumber,
      divisionNumber: divisionNumber,
      origin: 'Web',
      webName: webName,
      webEmail: userEmail,
      status: 'New',
      type: reqType,
      sendEmailNot: 'FALSE',
      difficultyLevel: 'High',
      recTypeId: recID,
      cesContCust: 'Yes',
      modelNbr: this.isCESProduct ? this.selectedParams.modelNumber : '',
      invNbr:
        this.selectedParams?.order?.orderHeader?.invoiceNumber?.toString(),
      invDate: this.selectedParams?.order?.orderHeader?.invoiceDate?.toString(),
    };
    let quantityUnits =
      this.selectedParams.quantityUnits === 'CS' ? 'Cases' : 'Each';

    if (this.selectedParams.quantityUnits && this.selectedParams.quantity) {
      let quantityValue = Number(this.selectedParams.quantity);
      if (quantityValue !== null && quantityUnits != '') {
        requestCredit['quantityAmt'] = quantityValue;
        requestCredit['quantityType'] = quantityUnits;
      }
    }
    if (this.isCESProduct) {
      requestCredit['serialNbr'] = this.selectedParams.serialNumber;
      requestCredit['quantityType'] = quantityUnits;
    }
    return requestCredit;
  }

  async convertFilesToAttachments() {
    let photo_array: RequestCreditAttachment[] = [];
    let counter = 0;
    for (const photo of this.selectedParams.photos) {
      try {
        let attachment = {
          name: this.createFileName(counter),
          type: photo.extension,
          value: await this.fileService.loadFileData(photo.filepath),
        };
        photo_array.push(attachment);
        counter += 1;
      } catch (ignore) {}
    }
    let file_array: RequestCreditAttachment[] = [];

    if (this.selectedParams.file) {
      try {
        let file_attachment = {
          name: this.createFileName(counter),
          type: this.getFileExtension(
            this.selectedParams.file ? this.selectedParams.file.filepath : null,
          ),
          value: await this.fileService.loadFileData(
            this.selectedParams.file ? this.selectedParams.file.filepath : null,
          ),
        };
        file_array.push(file_attachment);
      } catch (ignore) {}
    }
    return { file_array, photo_array };
  }

  createFileName(index: any) {
    return (
      '' +
      this.selectedParams.order.orderHeader.orderId +
      '-' +
      this.selectedParams.product.productNumber +
      '-' +
      index
    );
  }

  getFileExtension(filepath: string) {
    if (!filepath) {
      return null;
    }
    let temp_string: string[] = filepath.split('.');
    return temp_string[temp_string.length - 1];
  }

  // Lifecycle and Param Update Functions
  private async updateSelectedParams(paramUpdate: RequestCreditData) {
    if (
      this.selectedParams.file != undefined &&
      paramUpdate.file &&
      paramUpdate.file !== this.selectedParams.file
    ) {
      try {
        await this.fileService.deleteFile(this.selectedParams.file);
      } catch (errorToToast) {
        this.handleFileErrors(errorToToast.message);
      }
    }

    if (
      this.selectedParams.photos &&
      this.selectedParams.photos.length !== 0 &&
      paramUpdate.photos
    ) {
      await this.onPhotoChange(paramUpdate.photos);
    }

    this.selectedParams = paramUpdate;

    if (this.selectedParams.issue === undefined) {
      this.issueDetails = undefined;
    }

    if (!this.selectedParams.email) {
      this.selectedParams.email = paramUpdate.email;
    }

    this.setAttachmentRequired();
    this.setCommentsRequired();

    this.setSubmitButtonReady();
  }

  private async updateModalParams(paramUpdate: RequestCreditData) {
    this.modalRelatedParams = paramUpdate;
    if (this.modalRelatedParams.issue === undefined) {
      this.issueDetails = undefined;
    }
  }

  prepareForModal() {
    this.modalRelatedParams = this.selectedParams;
    this.issueDetailsForReset = this.issueDetails;
  }

  handleReset() {
    this.issueDetails = this.issueDetailsForReset;
  }

  finalizeParams() {
    this.updateSelectedParams(this.modalRelatedParams);
  }

  checkForCodes(codes: string[]) {
    if (!this.selectedParams.issueDetails) {
      return false;
    }

    let codeMatched = false;
    codes.forEach(code => {
      if (code == this.selectedParams.issueDetails.code) {
        codeMatched = true;
      }
    });
    return codeMatched;
  }

  setAttachmentRequired() {
    this.attachmentRequired =
      !this.checkForCodes(['01', '30']) && !this.isPickUp;
  }

  setCommentsRequired() {
    this.commentsRequired = this.checkForCodes(['30']);
  }

  requestParamsReset() {
    let toSet: RequestCreditData = {
      photos: [],
      file: undefined,
      quantityUnits: 'CS',
    } as RequestCreditData;
    this.updateModalParams(toSet);
    this.updateSelectedParams(toSet);

    try {
      this.photoService.deletePhotos(this.photoService.photos);
    } catch (ignore) {}
    try {
      this.fileService.deleteFiles(this.fileService.userFiles);
    } catch (ignore) {}
  }

  setTrackingData(trackingData: {
    orderId: string;
    hasPhoto: boolean;
    productData: any[];
    hasDocument: boolean;
  }) {
    this.trackingData$.next(trackingData);
  }

  getTrackingData() {
    return this.trackingData$.value;
  }

  setSubmitButtonReady() {
    let isProductSelected = !this.selectedParams?.product;
    let isEmailEmpty = !this.userEmail;
    let isIssueEmpty = !this.selectedParams?.issueDetails;
    let isProductRecievedPresentAndEmpty =
      this?.selectedParams?.issueDetails?.code === '04' &&
      !this.selectedParams.productReceived;
    let isCommentRequiredAndEmpty =
      this.commentsRequired && !this.selectedParams.comments;
    let isAttachmentRequiredAndImageOrFileAttached =
      this.attachmentRequired &&
      this.selectedParams.photos?.length === 0 &&
      this.selectedParams.file === undefined;
    let quantityForPickup =
      this.isPickUp &&
      (this.selectedParams.quantity === undefined ||
        this.selectedParams.quantity <= 0 ||
        this.selectedParams.quantityUnits === undefined);
    let resealCheck =
      this.isPickUp &&
      this.selectedParams.issueDetails?.productResaleable &&
      this.selectedParams.productInOriginalPackage === undefined;
    let blockSubmit =
      isProductSelected ||
      isEmailEmpty ||
      isIssueEmpty ||
      isCommentRequiredAndEmpty ||
      isProductRecievedPresentAndEmpty ||
      isAttachmentRequiredAndImageOrFileAttached ||
      quantityForPickup ||
      resealCheck;
    console.log('is the submission blocked by email: ', isEmailEmpty);
    this.blockSubmission.next(blockSubmit);
  }

  ngOnDestroy(): void {
    if (this.productNumbersSub$) {
      this.productNumbersSub$.unsubscribe();
    }
    if (this.productsSub$) {
      this.productsSub$.unsubscribe();
    }
    if (this.selectedProductSub$) {
      this.selectedProductSub$.unsubscribe();
    }
    if (this.searchCheckSub$) {
      this.searchCheckSub$.unsubscribe();
    }
  }
}
